/**
 * PMS Route Wrapper Components
 *
 * These wrappers bridge React Router (no-props route elements) and PMS
 * presentational components. All data flows through GraphQLPMSService
 * which calls AppSync → DynamoDB.
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { GraphQLPMSService } from '../../services/graphqlPms';
import { getObjectiveCycleId } from '../../utils/pmsFilters';
import { canonicalizeEmail } from '../../services/identity';
import { loadAllActiveEmployees, loadDirectReports, getUserByEmail } from '../../services/userLoader';
import { mapUserRoleToPMSRole, isPMSRoleHigherOrEqual } from '../../utils/pmsPermissions';
import { editReopensApproval, isResubmission, submittableObjectives } from '../../services/pmsObjectiveState';
import {
  notifyFinalReviewAvailable,
  notifyMidYearReviewReminder,
  notifyObjectiveApproved,
  notifyObjectiveChangeRequestApproved,
  notifyObjectiveChangeRequestRejected,
  notifyObjectiveReturned,
  notifyObjectiveSubmitted,
} from '../../services/inAppNotification';
import { UserRole } from '../../types/user';
import {
  PMSRole,
  ReviewType,
  ReviewCycleType,
  ReviewPhase,
  ReviewWorkflowStatus,
  ChatThreadType,
  AttachmentParentType,
  ObjectiveStatus,
  ReviewCycleStatus,
  Objective,
  Milestone,
  Review,
  ReviewCycle,
} from '../../types/pms';
import { canManagerReview, getSelfReviewProgress, isObjectiveLockedByHR, OBJECTIVES_LOCKED_MESSAGE, SelfReviewProgress } from '../../services/pmsWorkflow';

import EmployeeDashboard from './EmployeeDashboard';
import EmployeeSubmissionPanel from './EmployeeSubmissionPanel';
import ObjectiveForm from './ObjectiveForm';
import ObjectiveDetail from './ObjectiveDetail';
import RightSidePanel from './RightSidePanel';
import ManagerDashboard from './ManagerDashboard';
import ReviewForm from './ReviewForm';
import HRAdminDashboard from './HRAdminDashboard';
import ReviewCycleConfig from './ReviewCycleConfig';
import PerformanceReports from './PerformanceReports';
import ObjectiveLockManager from './ObjectiveLockManager';
import TeamReviews from './TeamReviews';
import InlineLoader from '../common/InlineLoader';
import './PendingApprovals.css';
import './EmployeeReviewsPage.css';

type PMSTeamMember = { id: string; email: string; name: string };
const PENDING_APPROVALS_CACHE_TTL_MS = 30000;
const pendingApprovalsRouteCache = new Map<string, { timestamp: number; objectives: Objective[] }>();

const normalizeEmail = (value?: string) => (value || '').trim().toLowerCase();

const isCurrentCycle = (cycle: ReviewCycle) => {
  const now = Date.now();
  const start = new Date(cycle.startDate || cycle.createdAt || 0).getTime();
  const end = new Date(cycle.endDate || 8640000000000000).getTime();
  return !Number.isNaN(start) && !Number.isNaN(end) && start <= now && now <= end;
};

const getCycleSortDate = (cycle: ReviewCycle) =>
  new Date(cycle.startDate || cycle.createdAt || 0).getTime() || 0;

/*
 * A locked cycle still belongs in the employee's list.
 *
 * Locking is meant to stop *editing*, not to hide the cycle. Excluding it here emptied
 * `allCycles` the moment HR locked objectives, which emptied `selectedCycleId`, which made the
 * loader below short-circuit and clear objectives and reviews — so the employee saw "No Active
 * Objective Cycle Available" and lost access to the review flow along with it, at exactly the
 * point in the year the reviews were about to start.
 *
 * EmployeeDashboard already handles the locked state properly and was simply never reached:
 * `isCycleLocked` reads the same flag, `isReadOnly` hides "+ New Objective" and disables the
 * editing paths, and there is a banner explaining the cycle is locked until HR unlocks it.
 * Read-only access is the correct outcome, so a locked cycle is admitted and that existing
 * handling does the rest.
 *
 * Both forms of "locked" have to be admitted: lockPMSObjectives writes `isLocked` *and* moves
 * status to Locked, so testing status alone would still have dropped it.
 */
const filterActiveObjectiveCycles = (cycles: ReviewCycle[]) =>
  cycles
    .filter((cycle) =>
      !cycle.annualCycleId &&
      (cycle.status === ReviewCycleStatus.ACTIVE || cycle.status === ReviewCycleStatus.LOCKED)
    )
    .sort((a, b) => {
      const currentRank = Number(isCurrentCycle(b)) - Number(isCurrentCycle(a));
      if (currentRank !== 0) return currentRank;
      return getCycleSortDate(b) - getCycleSortDate(a);
    });

const getPendingApprovalsCacheKey = (user: { email: string; role: UserRole }, employeeId?: string) =>
  `pendingApprovals|${user.role}|${normalizeEmail(user.email)}|${employeeId || ''}`;

const getCachedPendingApprovals = (key: string): Objective[] | null => {
  const cached = pendingApprovalsRouteCache.get(key);
  if (cached && Date.now() - cached.timestamp <= PENDING_APPROVALS_CACHE_TTL_MS) return cached.objectives;
  try {
    const stored = localStorage.getItem(`pms_${key}`);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!parsed || Date.now() - parsed.timestamp > PENDING_APPROVALS_CACHE_TTL_MS) return null;
    pendingApprovalsRouteCache.set(key, parsed);
    return parsed.objectives;
  } catch {
    return null;
  }
};

const setCachedPendingApprovals = (key: string, objectives: Objective[]) => {
  const entry = { timestamp: Date.now(), objectives };
  pendingApprovalsRouteCache.set(key, entry);
  try {
    localStorage.setItem(`pms_${key}`, JSON.stringify(entry));
  } catch {
    // Best-effort cache only; ignore storage quota or private mode failures.
  }
};

const clearPendingApprovalsCache = () => {
  pendingApprovalsRouteCache.clear();
  try {
    Object.keys(localStorage)
      .filter(key => key.startsWith('pms_pendingApprovals|'))
      .forEach(key => localStorage.removeItem(key));
  } catch {
    // Best-effort cache only.
  }
};

/*
 * Whether an objective belongs to someone on the manager's team.
 *
 * Compared on the CANONICAL address, not a lowercased one. Every employee has two accounts
 * since the creyentetech.com → creyenteinfotech.com move, and an objective written before it
 * carries the old-domain email while the roster reports the new one — see services/identity.ts.
 * A lowercased `===` therefore failed to match those rows and dropped them from the manager's
 * team scope entirely: they were filtered out before any count or table ever saw them, which
 * looks identical to the employee simply not having objectives.
 *
 * The caller builds memberEmails with the same canonicalisation, so both sides collapse to one
 * value per person whichever domain each was recorded under.
 */
const isObjectiveForTeamMember = (objective: Objective, memberEmails: Set<string>) => {
  const employeeId = canonicalizeEmail(objective.employeeId);
  return employeeId.length > 0 && memberEmails.has(employeeId);
};

const safeNotify = async (work: Promise<void>) => {
  try {
    await work;
  } catch (error) {
    console.error('[PMS Notifications] Failed to send notification:', error);
  }
};

// ─── Employee Dashboard Route ────────────────────────────────────────────────

export const PMSEmployeeDashboardRoute: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [allCycles, setAllCycles] = useState<ReviewCycle[]>([]);
  /*
   * Every cycle, parents and children alike.
   *
   * allCycles above is deliberately narrowed to active *objective* cycles because that is what
   * the page's selector offers. But the employee's reviews are filed against the CHILD review
   * cycles hanging off the selected objective cycle, so the child rows are needed too — see
   * childReviewCycles.
   */
  const [everyCycle, setEveryCycle] = useState<ReviewCycle[]>([]);
  const [selectedCycleId, setSelectedCycleId] = useState('');
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [milestonesByObjective, setMilestonesByObjective] = useState<Record<string, Milestone[]>>({});
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const progressPersistTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const milestoneProgressPersistTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const reload = useCallback(() => setRefreshKey(k => k + 1), []);

  const pmsRole = useMemo(
    () => (user ? mapUserRoleToPMSRole(user.role) : undefined),
    [user]
  );

  // Load active parent objective cycles for the employee objective selector.
  useEffect(() => {
    (async () => {
      const startedAt = performance.now();
      let cycles: ReviewCycle[] = [];
      try {
        cycles = await GraphQLPMSService.getAllReviewCycles();
      } catch (err: any) {
        // Recover partial data from AppSync partial errors
        const partial = err?.data?.listPMSReviewCycles?.items;
        if (partial && Array.isArray(partial)) {
          cycles = partial.filter((c: any) => c && c.id);
        }
      }
      const activeObjectiveCycles = filterActiveObjectiveCycles(cycles);
      console.log('[PMS][MyObjectives][Profile] Fetch Cycles', {
        durationMs: Math.round(performance.now() - startedAt),
        source: 'objective cycles (parent cycles without annualCycleId)',
        totalCycles: cycles.length,
        activeCycles: activeObjectiveCycles.length,
        excludedCycles: cycles.length - activeObjectiveCycles.length,
        displayCycles: activeObjectiveCycles.map((cycle) => ({
          id: cycle.id,
          name: cycle.name,
          status: cycle.status,
          isLocked: cycle.isLocked,
          annualCycleId: cycle.annualCycleId,
          startDate: cycle.startDate,
        })),
      });
      
      setEveryCycle(cycles);
      setAllCycles(activeObjectiveCycles);
      setSelectedCycleId(activeObjectiveCycles[0]?.id ?? '');
    })();
  }, []);

  /*
   * The review cycles that belong to the selected objective cycle.
   *
   * Reviews are never filed against the annual cycle itself — HR creates child review cycles
   * under it ("… review 1"), and a Review row carries that child's id. The dashboard needs the
   * child rows to answer two questions that were previously unanswerable here: does the
   * employee owe a self-review (which depends on the child's reviewPhase), and what is the
   * final rating.
   */
  const childReviewCycles = useMemo(
    () => (selectedCycleId ? everyCycle.filter(cycle => cycle.annualCycleId === selectedCycleId) : []),
    [everyCycle, selectedCycleId]
  );

  // Load objectives + milestones + reviews when cycle or user changes
  useEffect(() => {
    if (!user) { setLoading(false); return; }
    if (!selectedCycleId) {
      console.log('[PMS][MyObjectives][Profile] Empty selected cycle - rendering empty state');
      setObjectives([]);
      setReviews([]);
      setMilestonesByObjective({});
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      const renderStartedAt = performance.now();
      setLoading(true);
      try {
        const fetchStartedAt = performance.now();
        const [objsRaw, revsRaw] = await Promise.all([
          GraphQLPMSService.getEmployeeObjectives(user.email, selectedCycleId),
          GraphQLPMSService.getEmployeeReviews(user.email, selectedCycleId),
        ]);
        console.log('[PMS][MyObjectives][Profile] Fetch Data', {
          durationMs: Math.round(performance.now() - fetchStartedAt),
          selectedCycleId,
          objectiveCount: objsRaw.length,
          reviewCount: revsRaw.length,
          graphQLQueries: ['getEmployeeObjectives', 'getEmployeeReviews'],
        });

        const filteringStartedAt = performance.now();
        const objs = objsRaw.filter(obj => getObjectiveCycleId(obj) === selectedCycleId);
        /*
         * Reviews belong to the CHILD review cycles of the selected objective cycle.
         *
         * This used to be `review.reviewCycleId === selectedCycleId`, comparing a review's
         * child-cycle id against an annual-cycle id — two different id spaces, so it matched
         * nothing and `reviews` was permanently empty. That is the whole reason My Objectives
         * showed "Pending Reviews 0" and "Final Rating: Not Rated Yet" no matter what state the
         * employee's reviews were actually in: the metrics were computed over an empty array.
         *
         * getEmployeeReviews reports serverSideCycleFilter: false, i.e. it returns every review
         * for the employee whatever cycle id it is handed — so this client-side filter is the
         * only thing scoping them, and it has to name the child ids.
         *
         * selectedCycleId stays in the accepted set defensively: a review written directly
         * against an annual cycle (older data, before child cycles existed) still belongs here.
         */
        const cycleIdsForReviews = new Set([selectedCycleId, ...childReviewCycles.map(c => c.id)]);
        const revs = revsRaw.filter(review => cycleIdsForReviews.has(review.reviewCycleId));
        console.log('[PMS][MyObjectives][Profile] Filtering', {
          durationMs: Math.round(performance.now() - filteringStartedAt),
          selectedCycleId,
          filteredObjectiveCount: objs.length,
          filteredReviewCount: revs.length,
          removedObjectives: objsRaw.length - objs.length,
          removedReviews: revsRaw.length - revs.length,
        });

        if (cancelled) return;
        setObjectives(objs);
        setReviews(revs);
        setMilestonesByObjective({});
        setLoading(false);
        console.log('[PMS][MyObjectives][Profile] Rendering', {
          durationMs: Math.round(performance.now() - renderStartedAt),
          objectiveCount: objs.length,
          reviewCount: revs.length,
        });

        const enrichmentStartedAt = performance.now();
        const msEntries = await Promise.all(objs.map(async (obj) => {
          const milestoneStartedAt = performance.now();
          const milestones = await GraphQLPMSService.getMilestones(obj.id);
          console.log('[PMS][MyObjectives][Profile] GraphQL query getMilestones', {
            objectiveId: obj.id,
            durationMs: Math.round(performance.now() - milestoneStartedAt),
            milestoneCount: milestones.length,
          });
          return [obj.id, milestones] as const;
        }));
        if (cancelled) return;
        const nextMilestonesByObjective = msEntries.reduce<Record<string, Milestone[]>>((acc, [objectiveId, milestones]) => {
          acc[objectiveId] = milestones;
          return acc;
        }, {});
        setMilestonesByObjective(nextMilestonesByObjective);
        console.log('[PMS][MyObjectives][Profile] Enrichment', {
          durationMs: Math.round(performance.now() - enrichmentStartedAt),
          objectiveCount: objs.length,
          milestoneQueries: objs.length,
          nPlusOneDetected: objs.length > 1,
          s3Lookups: 0,
        });
      } catch (err: any) {
        console.error('[PMSEmployeeDashboard] Load error:', err);
        setObjectives([]);
        setReviews([]);
        setMilestonesByObjective({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // childReviewCycles is memoised on [everyCycle, selectedCycleId], so this re-runs only when
    // the cycle set genuinely changes — not on every render.
  }, [user, selectedCycleId, refreshKey, childReviewCycles]);

  const activeCycleId = useMemo(
    () => selectedCycleId || allCycles[0]?.id,
    [selectedCycleId, allCycles]
  );
  const dashboardManagerName = useManagerDisplayName(user?.managerEmail ?? undefined);

  const handleSaveGoal = useCallback(async (input: any, managerId: string, managerName: string, milestones: any[]) => {
    if (!user) return;
    // Enforce cycle-level lock
    const targetCycle = allCycles.find(c => c.id === (input.reviewCycleId || activeCycleId));
    if (targetCycle?.isLocked) {
      console.warn('[PMS] Cannot create objective — cycle is locked');
      return;
    }
    const result = await GraphQLPMSService.createObjective(input, user.email, user.name, managerId, managerName, true);
    /* Reloading a list the goal is not in is what hid this. See the objective form route. */
    if (!result.success || !result.entity) {
      throw new Error(result.errors?.[0] || 'The goal could not be saved.');
    }
    if (milestones.length > 0) {
      await Promise.all(milestones.map(ms =>
        GraphQLPMSService.createMilestone({ ...ms, objectiveId: result.entity!.id })
      ));
    }
    reload();
  }, [user, reload, allCycles, activeCycleId]);

  const handleUpdateGoal = useCallback(async (objectiveId: string, input: any, milestones: any[]) => {
    if (!user) return;
    // Find the existing objective to check if it was approved
    const existing = objectives.find(o => o.id === objectiveId);
    // Enforce cycle-level lock
    const parentCycle = allCycles.find(c => c.id === existing?.reviewCycleId);
    if (parentCycle?.isLocked || existing?.isLocked) {
      console.warn('[PMS] Cannot update objective — cycle is locked');
      return;
    }
    /*
     * Whether saving this edit should put the objective back in front of the manager.
     *
     * Was APPROVED || IN_PROGRESS, which left RETURNED out — so editing a returned objective in
     * response to the manager's comment left it RETURNED, and the employee had answered a
     * question nobody would ever see the answer to. editReopensApproval adds it, and says why
     * a DRAFT edit is deliberately not in the same list.
     */
    const wasApproved = editReopensApproval(existing?.status);
    const resubmitting = isResubmission(existing?.status);

    // Update the objective fields
    const updates: any = {
      title: input.title,
      description: input.description,
      measureOfSuccess: input.measureOfSuccess,
      startDate: input.startDate,
      endDate: input.endDate,
      weightage: input.weightage,
      employeeComments: input.employeeComments,
    };

    /*
     * Progress from the edit form, when it supplied one.
     *
     * Conditional so an edit that never touched progress cannot overwrite a figure the employee
     * has since moved with the row slider. Compared against undefined rather than truthiness
     * because 0% is a real answer.
     */
    if (input.progressPercentage !== undefined) {
      updates.progressPercentage = Math.round(Math.min(100, Math.max(0, Number(input.progressPercentage) || 0)));
    }

    // If the goal was approved/in-progress, re-submit for approval
    if (wasApproved) {
      updates.status = ObjectiveStatus.SUBMITTED;
    }

    await GraphQLPMSService.updateObjective(objectiveId, updates, {
      action: resubmitting
        ? 'Objective Updated and Resubmitted'
        : wasApproved
          ? 'Objective Updated and Submitted'
          : 'Objective Updated',
      changedBy: user.email,
      changedByName: user.name,
      previousObjective: existing,
    });
    if (wasApproved && existing) {
      await safeNotify(
        notifyObjectiveSubmitted(existing.managerId, user.name, existing.title, existing.id, resubmitting)
      );
    }

    // Handle milestones: delete existing, recreate
    const existingMs = milestonesByObjective[objectiveId] || [];
    if (existingMs.length > 0) {
      await Promise.all(existingMs.map(m => GraphQLPMSService.deleteMilestone(m.id)));
    }
    if (milestones.length > 0) {
      await Promise.all(milestones.map(ms =>
        GraphQLPMSService.createMilestone({ ...ms, objectiveId })
      ));
    }
    reload();
  }, [user, objectives, milestonesByObjective, reload, allCycles]);

  const handleSubmitAll = useCallback(async () => {
    if (!user) return;
    // Enforce cycle-level lock
    const activeCycle = allCycles.find(c => c.id === selectedCycleId);
    if (activeCycle?.isLocked) {
      console.warn('[PMS] Cannot submit objectives — cycle is locked');
      return;
    }
    /*
     * Drafts AND returned objectives.
     *
     * This filtered on DRAFT alone, so a returned objective could not be sent back however many
     * times the employee edited it — the one status that most obviously needs resubmitting was
     * the one status this refused. submittableObjectives is the shared rule; EmployeeDashboard
     * decides whether to render the bar from the same function, so the button and what it does
     * cannot part company.
     */
    const ready = submittableObjectives(objectives);
    await Promise.all(ready.map(obj => {
      // Read BEFORE the update, because that is what makes this a resubmission rather than a
      // first submission — and it is what the manager needs to be told.
      const resubmitting = isResubmission(obj.status);
      return GraphQLPMSService.updateObjective(obj.id, { status: ObjectiveStatus.SUBMITTED }, {
        action: resubmitting ? 'Resubmitted' : 'Submitted',
        changedBy: user.email,
        changedByName: user.name,
        previousObjective: obj,
      }).then(async () => {
        await safeNotify(
          notifyObjectiveSubmitted(obj.managerId, user.name, obj.title, obj.id, resubmitting)
        );
      });
    }));
    reload();
  }, [user, objectives, reload, allCycles, selectedCycleId]);

  const handleDelete = useCallback(async (objectiveId: string) => {
    // Enforce cycle-level lock
    const objective = objectives.find(o => o.id === objectiveId);
    const parentCycle = allCycles.find(c => c.id === objective?.reviewCycleId);
    if (parentCycle?.isLocked || objective?.isLocked) {
      console.warn('[PMS] Cannot delete objective — cycle is locked');
      return;
    }
    // Delete milestones first, then objective
    const ms = milestonesByObjective[objectiveId] || [];
    await Promise.all(ms.map(m => GraphQLPMSService.deleteMilestone(m.id)));
    await GraphQLPMSService.deleteObjective(objectiveId);
    reload();
  }, [milestonesByObjective, reload, objectives, allCycles]);

  const handleProgressChange = useCallback((objectiveId: string, value: number) => {
    if (!user) return;
    const progressPercentage = Math.round(Math.min(100, Math.max(0, value)));
    const objective = objectives.find(o => o.id === objectiveId);
    if (!objective || objective.isLocked) return;
    // Enforce cycle-level lock: check if the parent cycle is locked
    const parentCycle = allCycles.find(c => c.id === objective.reviewCycleId);
    if (parentCycle?.isLocked) return;
    const actorEmail = user.email;
    const actorName = user.name;

    const nextStatus =
      objective.status === ObjectiveStatus.APPROVED
        ? ObjectiveStatus.IN_PROGRESS
        : objective.status;

    setObjectives(prev => prev.map(o => (
      o.id === objectiveId
        ? { ...o, progressPercentage, status: nextStatus, updatedAt: new Date().toISOString() }
        : o
    )));

    if (progressPersistTimers.current[objectiveId]) {
      clearTimeout(progressPersistTimers.current[objectiveId]);
    }

    progressPersistTimers.current[objectiveId] = setTimeout(async () => {
      const updates: any = { progressPercentage };
      if (nextStatus !== objective.status) updates.status = nextStatus;
      const result = await GraphQLPMSService.updateObjective(objectiveId, updates, {
        action: 'Progress Updated',
        changedBy: actorEmail,
        changedByName: actorName,
        previousObjective: objective,
      });
      if (!result.success) {
        console.error('[PMSEmployeeDashboard] Failed to persist progress:', result.errors);
        reload();
      }
    }, 350);
  }, [objectives, reload, user, allCycles]);

  const handleMilestoneProgressChange = useCallback((objectiveId: string, milestoneId: string, value: number) => {
    if (!user) return;
    const progressPercentage = Math.round(Math.min(100, Math.max(0, value)));
    const objective = objectives.find(o => o.id === objectiveId);
    if (!objective || objective.isLocked) return;
    const parentCycle = allCycles.find(c => c.id === objective.reviewCycleId);
    if (parentCycle?.isLocked) return;
    const milestone = milestonesByObjective[objectiveId]?.find(ms => ms.id === milestoneId);
    if (!milestone) return;

    setMilestonesByObjective(prev => ({
      ...prev,
      [objectiveId]: (prev[objectiveId] || []).map(ms => (
        ms.id === milestoneId
          ? { ...ms, progressPercentage, updatedAt: new Date().toISOString() }
          : ms
      )),
    }));

    if (milestoneProgressPersistTimers.current[milestoneId]) {
      clearTimeout(milestoneProgressPersistTimers.current[milestoneId]);
    }

    milestoneProgressPersistTimers.current[milestoneId] = setTimeout(async () => {
      const result = await GraphQLPMSService.updateMilestone(milestoneId, { progressPercentage });
      if (!result.success) {
        console.error('[PMSEmployeeDashboard] Failed to persist milestone progress:', result.errors);
        reload();
      }
    }, 350);
  }, [allCycles, milestonesByObjective, objectives, reload, user]);

  useEffect(() => () => {
    Object.values(progressPersistTimers.current).forEach(clearTimeout);
    Object.values(milestoneProgressPersistTimers.current).forEach(clearTimeout);
  }, []);

  if (!user || !pmsRole) return null;
  if (loading && objectives.length === 0) return <InlineLoader message="Loading objectives" />;

  return (
    <EmployeeDashboard
      employeeId={user.email}
      employeeName={user.name}
      userRole={pmsRole}
      objectives={objectives}
      milestonesByObjective={milestonesByObjective}
      reviews={reviews}
      reviewCycles={allCycles}
      childReviewCycles={childReviewCycles}
      selectedCycleId={selectedCycleId}
      onCycleChange={setSelectedCycleId}
      onViewReview={() => navigate('/performance/reviews')}
      getVersions={async (id) => GraphQLPMSService.getObjectiveVersions(id)}
      getChatMessages={async (id) => GraphQLPMSService.getMessages(ChatThreadType.OBJECTIVE, id)}
      getAttachments={async (id) => GraphQLPMSService.getAttachments(AttachmentParentType.OBJECTIVE, id)}
      onDeleteObjective={handleDelete}
      onProgressChange={handleProgressChange}
      onMilestoneProgressChange={handleMilestoneProgressChange}
      onSaveGoal={handleSaveGoal}
      onUpdateGoal={handleUpdateGoal}
      onSubmitAll={handleSubmitAll}
      onSendMessage={async (objectiveId, content) => {
        await GraphQLPMSService.sendMessage(
          { threadType: ChatThreadType.OBJECTIVE, threadId: objectiveId, content },
          user.email, user.name
        );
      }}
      onUploadAttachment={async (objectiveId, file) => {
        await GraphQLPMSService.addAttachment(
          {
            parentType: AttachmentParentType.OBJECTIVE,
            parentId: objectiveId,
            fileName: file.name,
            fileType: file.type || file.name.split('.').pop() || '',
            fileSize: file.size,
            s3Key: `pms-objectives/${objectiveId}/attachments/${file.name}`,
          },
          user.email
        );
      }}
      reviewCycleIdForCreate={activeCycleId}
      defaultManagerId={user.managerEmail ?? ''}
      defaultManagerName={dashboardManagerName}
    />
  );
};


/**
 * The manager's display name for `managerEmail`, from User Management.
 *
 * Objective creation stores managerId and managerName side by side, but only the id was ever
 * resolved — the form passed a literal '' for the name, so createObjective fell back to its
 * 'Unassigned' placeholder and every objective carried it. The placeholder then travelled onto the
 * review at self-review submission and surfaced as "Manager: Unassigned". Resolving the name from
 * the same directory record the id comes from keeps the pair consistent at the point of writing.
 *
 * Returns '' until it resolves, and on failure. createObjective still has its placeholder for that
 * case, and resolveReviewerIdentity discards it downstream, so a slow or unreadable directory
 * degrades to the previous behaviour rather than blocking the save.
 */
function useManagerDisplayName(managerEmail?: string): string {
  const [managerName, setManagerName] = useState('');
  useEffect(() => {
    if (!managerEmail) { setManagerName(''); return; }
    let cancelled = false;
    (async () => {
      const manager = await getUserByEmail(managerEmail);
      if (!cancelled) setManagerName(manager?.fullName || manager?.name || '');
    })();
    return () => { cancelled = true; };
  }, [managerEmail]);
  return managerName;
}

// ─── Objective Form Route ────────────────────────────────────────────────────

export const PMSObjectiveFormRoute: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeCycleId, setActiveCycleId] = useState<string | undefined>();
  const [existingTotal, setExistingTotal] = useState(0);
  const defaultManagerName = useManagerDisplayName(user?.managerEmail ?? undefined);

  useEffect(() => {
    if (!user) return;
    (async () => {
      // Get the active annual cycle (parent cycle, no annualCycleId)
      const active = await GraphQLPMSService.getActiveAnnualCycle();
      if (active) {
        setActiveCycleId(active.id);
        const objs = await GraphQLPMSService.getEmployeeObjectives(user.email, active.id);
        setExistingTotal(objs.reduce((sum, o) => sum + o.weightage, 0));
      }
    })();
  }, [user]);

  if (!user || !activeCycleId) return null;

  return (
    <ObjectiveForm
      existingWeightageTotal={existingTotal}
      reviewCycleId={activeCycleId}
      defaultManagerId={user.managerEmail ?? ''}
      defaultManagerName={defaultManagerName}
      onSave={async (input, managerId, managerName, milestones) => {
        const result = await GraphQLPMSService.createObjective(input, user.email, user.name, managerId, managerName, true);
        /*
         * Navigate only when the goal actually saved.
         *
         * This used to navigate either way, which is why a failed create was indistinguishable
         * from a successful one: the employee landed back on My Objectives, found nothing
         * there, and had no error to read and no filled-in form left to retry from.
         */
        if (!result.success || !result.entity) {
          throw new Error(result.errors?.[0] || 'The goal could not be saved.');
        }
        if (milestones.length > 0) {
          await Promise.all(milestones.map(ms =>
            GraphQLPMSService.createMilestone({ ...ms, objectiveId: result.entity!.id })
          ));
        }
        navigate('/performance/objectives');
      }}
      onCancel={() => navigate('/performance/objectives')}
    />
  );
};

// ─── Objective Detail Route ──────────────────────────────────────────────────

export const PMSObjectiveDetailRoute: React.FC = () => {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [objective, setObjective] = useState<Objective | undefined>();
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [versions, setVersions] = useState<any[]>([]);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const pmsRole = useMemo(
    () => (user ? mapUserRoleToPMSRole(user.role) : undefined),
    [user]
  );

  const reload = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    if (!id) return;
    (async () => {
      const [obj, ms, vers, msgs, atts] = await Promise.all([
        GraphQLPMSService.getObjective(id),
        GraphQLPMSService.getMilestones(id),
        GraphQLPMSService.getObjectiveVersions(id),
        GraphQLPMSService.getMessages(ChatThreadType.OBJECTIVE, id),
        GraphQLPMSService.getAttachments(AttachmentParentType.OBJECTIVE, id),
      ]);
      setObjective(obj);
      setMilestones(ms);
      setVersions(vers);
      setChatMessages(msgs);
      setAttachments(atts);
    })();
  }, [id, refreshKey]);

  const isManager = useMemo(() => {
    if (!user || !objective || !pmsRole) return false;
    return objective.managerId === user.email || isPMSRoleHigherOrEqual(pmsRole, PMSRole.HR_ADMIN);
  }, [user, objective, pmsRole]);
  const isObjectiveOwner = user?.email === objective?.employeeId;

  if (!user || !pmsRole || !objective) return <p>Objective not found.</p>;

  return (
    <ObjectiveDetail
      objective={objective}
      milestones={milestones}
      versions={versions}
      chatMessages={chatMessages}
      attachments={attachments}
      userRole={pmsRole}
      userId={user.email}
      onProgressChange={isObjectiveOwner ? async (value: number) => {
        const progressPercentage = Math.round(Math.min(100, Math.max(0, value)));
        const previousObjective = objective;
        setObjective({ ...objective, progressPercentage, updatedAt: new Date().toISOString() });
        const result = await GraphQLPMSService.updateObjective(objective.id, { progressPercentage }, {
          action: 'Progress Updated',
          changedBy: user.email,
          changedByName: user.name,
          previousObjective,
        });
        if (!result.success) reload();
      } : undefined}
      onApprove={isManager ? async () => {
        await GraphQLPMSService.updateObjective(objective.id, { status: ObjectiveStatus.APPROVED }, {
          action: objective.managerComments ? 'Objective Change Request Approved' : 'Approved',
          changedBy: user.email,
          changedByName: user.name,
          previousObjective: objective,
        });
        await safeNotify(
          objective.managerComments
            ? notifyObjectiveChangeRequestApproved(objective.employeeId, user.name, objective.title, objective.id)
            : notifyObjectiveApproved(objective.employeeId, user.name, objective.title, objective.id)
        );
        reload();
      } : undefined}
      onReturn={isManager ? async (comments: string) => {
        await GraphQLPMSService.updateObjective(objective.id, { status: ObjectiveStatus.RETURNED, managerComments: comments }, {
          action: objective.managerComments ? 'Objective Change Request Rejected' : 'Returned',
          changedBy: user.email,
          changedByName: user.name,
          previousObjective: objective,
        });
        await safeNotify(
          objective.managerComments
            ? notifyObjectiveChangeRequestRejected(objective.employeeId, user.name, objective.title, objective.id, comments)
            : notifyObjectiveReturned(objective.employeeId, user.name, objective.title, objective.id, comments)
        );
        reload();
      } : undefined}
      onSendMessage={async (content: string) => {
        await GraphQLPMSService.sendMessage(
          { threadType: ChatThreadType.OBJECTIVE, threadId: objective.id, content },
          user.email, user.name
        );
        reload();
      }}
      onUploadAttachment={async (file: File) => {
        await GraphQLPMSService.addAttachment(
          {
            parentType: AttachmentParentType.OBJECTIVE,
            parentId: objective.id,
            fileName: file.name,
            fileType: file.type || file.name.split('.').pop() || '',
            fileSize: file.size,
            s3Key: `pms-objectives/${objective.id}/attachments/${file.name}`,
          },
          user.email
        );
        reload();
      }}
    />
  );
};

// ─── Review Detail Route (now renders TeamReviews) ───────────────────────────

export const PMSReviewDetailRoute: React.FC = () => {
  return (
    <div className="pms-employee-reviews-route">
      <TeamReviews mode="my" />
    </div>
  );
};

// ─── Team Reviews Route ──────────────────────────────────────────────────────

export const PMSTeamReviewsRoute: React.FC = () => {
  return <TeamReviews mode="team" />;
};

// ─── Manager Dashboard Route ─────────────────────────────────────────────────

export const PMSManagerDashboardRoute: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [allCycles, setAllCycles] = useState<ReviewCycle[]>([]);
  const [selectedCycleId, setSelectedCycleId] = useState('');
  const [teamObjectives, setTeamObjectives] = useState<Objective[]>([]);
  const [teamReviews, setTeamReviews] = useState<Review[]>([]);
  const [teamMembers, setTeamMembers] = useState<PMSTeamMember[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');

  useEffect(() => {
    (async () => {
      const cycles = await GraphQLPMSService.getAllReviewCycles();
      // Only show ACTIVE annual cycles (parent) in the dropdown
      const annualOnly = cycles.filter(c => !c.annualCycleId);
      const activeCycles = annualOnly.filter(c => c.status === ReviewCycleStatus.ACTIVE);
      setAllCycles(activeCycles);
      setSelectedCycleId(activeCycles[0]?.id ?? '');
    })();
  }, []);

  // Fetch visible team members. SUPER_ADMIN is global; managers remain direct-report scoped.
  useEffect(() => {
    if (!user) return;
    (async () => {
      const members: PMSTeamMember[] = user.role === UserRole.SUPER_ADMIN
        ? (await loadAllActiveEmployees(user.email)).map(member => ({
            id: member.email,
            email: member.email,
            name: member.name,
          }))
        : (await loadDirectReports(user.email)).map(member => ({
            id: member.email,
            email: member.email,
            name: member.name,
          }));
      setTeamMembers(members);
    })();
  }, [user]);

  useEffect(() => {
    if (!user || teamMembers.length === 0) return;
    (async () => {
      // Load objectives per employee directly — avoids the parent/child cycle ID mismatch
      // (getObjectivesByReviewCycle uses child reviewCycleId; selectedCycleId is a parent annual ID)
      const MANAGER_VISIBLE_STATUSES = [
        ObjectiveStatus.SUBMITTED,
        ObjectiveStatus.APPROVED,
        ObjectiveStatus.RETURNED,
        ObjectiveStatus.IN_PROGRESS,
        ObjectiveStatus.UNDER_REVIEW,
        ObjectiveStatus.COMPLETED,
      ];

      // Canonicalised to match isObjectiveForTeamMember — see its docstring.
      const memberEmails = new Set(teamMembers.map(m => canonicalizeEmail(m.email)).filter(Boolean));

      const allObjArrays = await Promise.all(
        teamMembers.map(m => GraphQLPMSService.getEmployeeObjectives(m.email))
      );
      /*
       * Deduplicate by objective id across members.
       *
       * getEmployeeObjectives already dedupes within one call (collectAcrossIdentities merges the
       * two email-domain aliases by id), but this flattens N per-member calls with no guard — so
       * one person appearing twice in the roster, under either domain, contributed their whole
       * objective list twice to the team total.
       */
      const seenObjectiveIds = new Set<string>();
      const allObjs = allObjArrays.flat().filter((objective) => {
        if (!objective?.id) return false;
        if (seenObjectiveIds.has(objective.id)) return false;
        seenObjectiveIds.add(objective.id);
        return true;
      });
      const scopedObjectives = user.role === UserRole.SUPER_ADMIN
        ? allObjs
        : allObjs.filter(o => isObjectiveForTeamMember(o, memberEmails));
      const visibleObjectives = scopedObjectives.filter(o => MANAGER_VISIBLE_STATUSES.includes(o.status));
      setTeamObjectives(visibleObjectives);

      /*
       * Where the headline objective count comes from, broken down.
       *
       * This fetch is deliberately NOT scoped to selectedCycleId — one fetch serves every cycle
       * so switching the dropdown is instant — which means this array spans the team's whole
       * history and must never be rendered as a count on its own. ManagerDashboard narrows it
       * with uniqueObjectivesForCycle. This log exists so the two numbers can be reconciled
       * against real data instead of guessed at.
       */
      const byCycle = new Map<string, Map<string, string[]>>();
      for (const objective of visibleObjectives) {
        const cycleId = getObjectiveCycleId(objective) || '(no cycle id)';
        const employee = normalizeEmail(objective.employeeId) || '(no employee)';
        if (!byCycle.has(cycleId)) byCycle.set(cycleId, new Map());
        const perEmployee = byCycle.get(cycleId)!;
        perEmployee.set(employee, [...(perEmployee.get(employee) || []), objective.title]);
      }
      console.groupCollapsed('[PMS][TeamPerformance] Objective count breakdown');
      console.info({
        teamMembers: teamMembers.length,
        fetchedRows: allObjArrays.flat().length,
        afterIdDedupe: allObjs.length,
        afterTeamScope: scopedObjectives.length,
        afterStatusFilter: visibleObjectives.length,
        note: 'afterStatusFilter spans ALL objective cycles — the page counts one cycle only.',
      });
      byCycle.forEach((perEmployee, cycleId) => {
        const total = Array.from(perEmployee.values()).reduce((sum, titles) => sum + titles.length, 0);
        console.groupCollapsed(`Objective cycle ${cycleId} — ${total} unique objective(s)`);
        perEmployee.forEach((titles, employee) => {
          console.info(`${employee}: ${titles.length}`, titles);
        });
        console.groupEnd();
      });
      console.groupEnd();

      // Load reviews — use all cycle IDs that appear in the loaded objectives
      // so review badges can match correctly regardless of parent/child cycle hierarchy
      const cycleIdsInObjectives = Array.from(new Set(scopedObjectives.map(o => o.reviewCycleId).filter(Boolean)));
      const revArrays = await Promise.all(
        cycleIdsInObjectives.map(cid => GraphQLPMSService.getReviewsByReviewCycle(cid))
      );
      const allRevs = revArrays.flat();
      setTeamReviews(user.role === UserRole.SUPER_ADMIN
        ? allRevs
        // canonicalizeEmail, matching how memberEmails is built — see isObjectiveForTeamMember.
        : allRevs.filter(r => memberEmails.has(canonicalizeEmail(r.employeeId)))
      );
    })();
  }, [user, teamMembers]);

  if (!user) return null;

  return (
    <ManagerDashboard
      managerId={user.email}
      teamObjectives={teamObjectives}
      teamReviews={teamReviews}
      reviewCycles={allCycles}
      selectedCycleId={selectedCycleId}
      onCycleChange={setSelectedCycleId}
      teamMembers={teamMembers}
      selectedMemberId={selectedMemberId}
      onMemberChange={setSelectedMemberId}
      onTeamMemberClick={(empId) => navigate(`/performance/team/${empId}`)}
    />
  );
};

// ─── Objective List Route (team/:employeeId and approvals) ───────────────────

export const PMSObjectiveListRoute: React.FC = () => {
  const { user } = useAuth();
  const { employeeId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();
  /**
   * `?objective=<id>` — where a manager's notification lands.
   *
   * Every PMS notification used to link to `/performance/objectives/<id>`, whoever it was for,
   * and the sidebar marks an entry active by path prefix — so a manager reviewing somebody
   * else's goal watched MY OBJECTIVES light up. Putting the destination in the address rather
   * than in the notification handler is what makes the click, a refresh and a pasted link all
   * behave the same: this page is the approvals queue whether you arrived by menu or by URL.
   */
  const [searchParams] = useSearchParams();
  const requestedObjectiveId = searchParams.get('objective');
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [expandedApprovalEmployee, setExpandedApprovalEmployee] = useState<string | null>(null);
  const [selectedObjective, setSelectedObjective] = useState<Objective | null>(null);
  const [detailMilestones, setDetailMilestones] = useState<Milestone[]>([]);
  const [detailVersions, setDetailVersions] = useState<any[]>([]);
  const [detailChat, setDetailChat] = useState<any[]>([]);
  const [detailAttachments, setDetailAttachments] = useState<any[]>([]);

  /*
   * Review cycles, loaded solely to answer "has HR locked this objective's cycle".
   *
   * This route had no cycle awareness at all, so its approve/return actions were gated only on
   * objective.status === Submitted. After HR locked a cycle a manager could still approve or
   * return objectives from here — the lock stopped the employee editing but not the manager
   * acting, which is not what "objectives become read-only" means.
   */
  const [cycles, setCycles] = useState<ReviewCycle[]>([]);

  useEffect(() => {
    let active = true;
    GraphQLPMSService.getAllReviewCycles()
      .then(list => { if (active) setCycles(list); })
      // A failed cycle load must not block the approvals list. It fails closed below:
      // with no cycle in hand isObjectiveLockedByHR reports unlocked, which is the
      // pre-existing behaviour rather than a new way to be stuck.
      .catch(() => { if (active) setCycles([]); });
    return () => { active = false; };
  }, [refreshKey]);

  /** Whether HR's lock covers this objective, via its own cycle. */
  const isLockedByHR = useCallback(
    (objective?: Objective | null) => isObjectiveLockedByHR(
      objective,
      cycles.find(c => c.id === objective?.reviewCycleId)
    ),
    [cycles]
  );

  const reload = useCallback(() => { setRefreshKey(k => k + 1); setSelectedObjective(null); }, []);
  const renderStart = performance.now();

  useEffect(() => {
    console.log('[PendingApprovals][Perf] Render', {
      durationMs: Math.round(performance.now() - renderStart),
      loading,
      objectiveCount: objectives.length,
      employeeId: employeeId || null,
    });
  });

  useEffect(() => {
    if (!user) return;
    const cacheKey = getPendingApprovalsCacheKey(user, employeeId);
    const cached = getCachedPendingApprovals(cacheKey);
    if (cached) {
      setObjectives(cached);
      setLoading(false);
      console.log('[PendingApprovals][Perf] Cache hit', {
        cacheKey,
        objectiveCount: cached.length,
      });
    } else {
      setLoading(true);
    }
    const routeStart = performance.now();
    const traceId = `pending-approvals-${Math.round(routeStart)}`;
    console.time(`[PendingApprovals][Perf] ${traceId} route`);
    console.log('[PendingApprovals][Perf] Route start', {
      traceId,
      userId: user.id,
      userEmail: user.email,
      role: user.role,
      employeeId: employeeId || null,
      cacheHit: Boolean(cached),
    });
    (async () => {
      const stageStats = {
        fetchDataMs: 0,
        enrichmentMs: 0,
        mappingMs: 0,
        filteringMs: 0,
        graphQLQueries: [] as string[],
        s3Lookups: 0,
        nPlusOneDetected: false,
      };
      try {
        if (employeeId) {
          // When manager views a team member's objectives, exclude Draft
          const objectivesStart = performance.now();
          const objs = await GraphQLPMSService.getEmployeeObjectives(employeeId);
          stageStats.fetchDataMs += Math.round(performance.now() - objectivesStart);
          stageStats.graphQLQueries.push('getEmployeeObjectives');
          console.log('[PendingApprovals][Perf] Objectives query', {
            traceId,
            operation: 'getEmployeeObjectives',
            durationMs: Math.round(performance.now() - objectivesStart),
            rawCount: objs.length,
          });
          const filteringStart = performance.now();
          const filtered = objs.filter(o => o.status !== ObjectiveStatus.DRAFT);
          stageStats.filteringMs += Math.round(performance.now() - filteringStart);
          setObjectives(filtered);
          setCachedPendingApprovals(cacheKey, filtered);
          console.log('[PendingApprovals][Perf] Objective filtering', {
            traceId,
            durationMs: Math.round(performance.now() - filteringStart),
            filteredCount: filtered.length,
          });
        } else {
          // Pending approvals — load ALL objectives for this manager's team (all statuses except Draft)
          if (user.role === UserRole.SUPER_ADMIN || user.role === UserRole.HR_ADMIN) {
            const objectivesStart = performance.now();
            const all = await GraphQLPMSService.getAllObjectives();
            stageStats.fetchDataMs += Math.round(performance.now() - objectivesStart);
            stageStats.graphQLQueries.push('getAllObjectives');
            console.log('[PendingApprovals][Perf] Objectives query', {
              traceId,
              operation: 'getAllObjectives',
              durationMs: Math.round(performance.now() - objectivesStart),
              rawCount: all.length,
              mode: user.role === UserRole.SUPER_ADMIN ? 'super_admin' : 'hr_admin',
            });
            const filteringStart = performance.now();
            // Show all statuses except Draft for admin view
            const filtered = all.filter(o => o.status !== ObjectiveStatus.DRAFT);
            stageStats.filteringMs += Math.round(performance.now() - filteringStart);
            setObjectives(filtered);
            setCachedPendingApprovals(cacheKey, filtered);
            console.log('[PendingApprovals][Perf] Objective filtering', {
              traceId,
              durationMs: Math.round(performance.now() - filteringStart),
              filteredCount: filtered.length,
              mode: user.role === UserRole.SUPER_ADMIN ? 'super_admin' : 'hr_admin',
            });
          } else {
            const managerObjectivesStart = performance.now();
            const managerObjectives = await GraphQLPMSService.getObjectivesByManager(user.email);
            stageStats.fetchDataMs += Math.round(performance.now() - managerObjectivesStart);
            stageStats.graphQLQueries.push('getObjectivesByManager');
            console.log('[PendingApprovals][Perf] Objectives query', {
              traceId,
              operation: 'getObjectivesByManager',
              durationMs: Math.round(performance.now() - managerObjectivesStart),
              rawCount: managerObjectives.length,
              mode: 'manager_index',
            });

            // Keep ALL statuses except Draft — filters handle display
            let filtered = managerObjectives.filter(o => o.status !== ObjectiveStatus.DRAFT);
            const filteringStart = performance.now();
            let directReportCount = 0;

            if (filtered.length === 0) {
              const fallbackStart = performance.now();
              const members = await loadDirectReports(user.email);
              directReportCount = members.length;
              stageStats.enrichmentMs += Math.round(performance.now() - fallbackStart);
              console.log('[PendingApprovals][Profile] Employee lookup/cache', {
                traceId,
                durationMs: Math.round(performance.now() - fallbackStart),
                directReportCount,
                repeatedEmployeeLookup: false,
              });
              if (members.length > 0) {
                // Fetch objectives for each direct report
                const memberEmails = members.map(m => m.email);
                const fanOutStart = performance.now();
                const allObjArrays = await Promise.all(
                  memberEmails.map(email => GraphQLPMSService.getEmployeeObjectives(email))
                );
                stageStats.fetchDataMs += Math.round(performance.now() - fanOutStart);
                stageStats.graphQLQueries.push(`getEmployeeObjectives x${memberEmails.length}`);
                stageStats.nPlusOneDetected = memberEmails.length > 1;
                filtered = allObjArrays.flat().filter(o => o.status !== ObjectiveStatus.DRAFT);
                if (stageStats.nPlusOneDetected) {
                  console.warn('[PendingApprovals][Profile] N+1 objective lookups detected', {
                    traceId,
                    durationMs: Math.round(performance.now() - fanOutStart),
                    employeeQueryCount: memberEmails.length,
                    objectiveCount: filtered.length,
                  });
                }
              }
              console.log('[PendingApprovals][Perf] Manager fallback query', {
                traceId,
                durationMs: Math.round(performance.now() - fallbackStart),
                directReportCount,
                filteredCount: filtered.length,
              });
            }

            setObjectives(filtered);
            setCachedPendingApprovals(cacheKey, filtered);
            stageStats.filteringMs += Math.round(performance.now() - filteringStart);
            console.log('[PendingApprovals][Perf] Objective filtering', {
              traceId,
              durationMs: Math.round(performance.now() - filteringStart),
              filteredCount: filtered.length,
              directReportCount,
              mode: directReportCount > 0 ? 'direct_reports_fallback' : 'manager_index',
            });
          }
        }
      } finally {
        setLoading(false);
        console.log('[PendingApprovals][Profile] Timing Report', {
          traceId,
          fetchDataMs: stageStats.fetchDataMs,
          enrichmentMs: stageStats.enrichmentMs,
          mappingMs: stageStats.mappingMs,
          filteringMs: stageStats.filteringMs,
          renderingMs: Math.round(performance.now() - routeStart),
          graphQLQueries: stageStats.graphQLQueries,
          s3Lookups: stageStats.s3Lookups,
          nPlusOneDetected: stageStats.nPlusOneDetected,
        });
        console.log('[PendingApprovals][Perf] Route complete', {
          traceId,
          durationMs: Math.round(performance.now() - routeStart),
        });
        console.timeEnd(`[PendingApprovals][Perf] ${traceId} route`);
      }
    })();
  }, [user, employeeId, refreshKey]);

  const approvalFilters = useMemo(
    () => ['All', 'Draft', 'Submitted', 'Approved', 'Returned', 'In_Progress', 'Under_Review', 'Completed'],
    []
  );

  const approvalSummary = useMemo(() => {
    const counts = new Map<string, number>();
    approvalFilters.forEach(filter => counts.set(filter, 0));
    counts.set('All', objectives.length);
    objectives.forEach(objective => {
      counts.set(objective.status, (counts.get(objective.status) || 0) + 1);
    });

    const filtered = statusFilter === 'All' ? objectives : objectives.filter(o => o.status === statusFilter);
    /*
     * ── One group per PERSON, not per address ───────────────────────────────────────────────
     *
     * This keyed on the raw `employeeId` string, and that is why one employee appeared twice in
     * the queue — "13 objectives, 64% avg" above "1 objective, 69% avg", same name both times.
     *
     * Nothing is duplicated in the database. getObjectivesByManager runs through
     * collectAcrossIdentities, which queries under BOTH of the manager's domain addresses and
     * merges the rows (deduping by id, so no row is counted twice). But each objective is filed
     * under whichever address the EMPLOYEE held when it was created, and the domain move left
     * people holding two. Two ids for one human, so two groups — and a manager reading that had
     * no way to tell it from two different people with the same name.
     *
     * canonicalizeEmail is the same key the rest of the product groups people by; using it here
     * is what makes this page agree with every other screen about how many employees there are.
     */
    const grouped = new Map<string, Objective[]>();
    filtered.forEach(obj => {
      const key = canonicalizeEmail(obj.employeeId) || obj.employeeId;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(obj);
    });

    return {
      counts,
      groupedEntries: Array.from(grouped.entries()),
    };
  }, [approvalFilters, objectives, statusFilter]);

  const handleObjectiveClick = useCallback(async (obj: Objective) => {
    const startedAt = performance.now();
    setSelectedObjective(obj);
    // Load detail data
    const fetchStartedAt = performance.now();
    const [ms, vers, msgs, atts] = await Promise.all([
      GraphQLPMSService.getMilestones(obj.id),
      GraphQLPMSService.getObjectiveVersions(obj.id),
      GraphQLPMSService.getMessages(ChatThreadType.OBJECTIVE, obj.id),
      GraphQLPMSService.getAttachments(AttachmentParentType.OBJECTIVE, obj.id),
    ]);
    console.log('[PendingApprovals][DetailProfile] Timing Report', {
      objectiveId: obj.id,
      fetchDataMs: Math.round(performance.now() - fetchStartedAt),
      enrichmentMs: 0,
      mappingMs: 0,
      renderingMs: Math.round(performance.now() - startedAt),
      graphQLQueries: ['getMilestones', 'getObjectiveVersions', 'getMessages', 'getAttachments'],
      s3Lookups: 0,
      receiptLookupsPerformedIndividually: false,
      objectiveLookupsRepeated: false,
    });
    setDetailMilestones(ms);
    setDetailVersions(vers);
    setDetailChat(msgs);
    setDetailAttachments(atts);
  }, []);

  /**
   * Open the objective a notification pointed at, once the queue has loaded.
   *
   * Guarded on `selectedObjective` being empty so this opens it once and then leaves the manager
   * alone — without that, closing the panel would immediately reopen it and the back button
   * would be unusable. The id staying in the address is deliberate: a refresh should land on the
   * same objective, which is the whole reason it is in the URL and not in navigation state.
   *
   * A notification for an objective that is no longer in this queue — decided by a colleague
   * while the mail sat unread — simply opens the queue, which is the honest outcome.
   */
  const openedFromLink = useRef(false);
  useEffect(() => {
    if (!requestedObjectiveId || openedFromLink.current || loading) return;
    const match = objectives.find(objective => objective.id === requestedObjectiveId);
    if (!match) return;
    openedFromLink.current = true;
    setExpandedApprovalEmployee(canonicalizeEmail(match.employeeId) || match.employeeId);
    handleObjectiveClick(match);
  }, [requestedObjectiveId, objectives, loading, handleObjectiveClick]);

  const applyObjectiveStatusLocally = useCallback((
    objectiveId: string,
    updates: Pick<Objective, 'status'> & Partial<Objective>
  ) => {
    setObjectives(prev => prev.map(objective => (
      objective.id === objectiveId
        ? { ...objective, ...updates, updatedAt: new Date().toISOString() }
        : objective
    )));
    setSelectedObjective(prev => (
      prev?.id === objectiveId
        ? { ...prev, ...updates, updatedAt: new Date().toISOString() }
        : prev
    ));
  }, []);

  const handleApprove = useCallback(async (objectiveId: string) => {
    const objective = objectives.find(o => o.id === objectiveId);
    // Re-checked here, not only where the button is rendered: the props below are the
    // affordance, this is the boundary.
    if (isLockedByHR(objective)) {
      console.warn('[PMSObjectiveList] Refused to approve a locked objective:', objectiveId);
      return;
    }
    const result = await GraphQLPMSService.updateObjective(objectiveId, { status: ObjectiveStatus.APPROVED }, {
      action: objective?.managerComments ? 'Objective Change Request Approved' : 'Approved',
      changedBy: user!.email,
      changedByName: user!.name,
      previousObjective: objective,
    });
    /*
     * The write has to be confirmed before anything downstream runs.
     *
     * updateObjective reports failure by returning { success: false } — it does not throw — and
     * this ignored the result. So a rejected write still notified the employee that their
     * objective was approved, still flipped the row to Approved locally, and still cleared it
     * out of Pending Approvals. The approval existed only in the manager's browser until the
     * next refresh put it back to Submitted, with an approval notification already sent.
     *
     * reload() on failure resyncs from the server rather than leaving the optimistic state —
     * the same pattern handleProgressChange above uses.
     */
    if (!result.success) {
      console.error('[PMSObjectiveList] Failed to approve objective:', result.errors);
      reload();
      return;
    }
    if (objective) {
      await safeNotify(
        objective.managerComments
          ? notifyObjectiveChangeRequestApproved(objective.employeeId, user!.name, objective.title, objective.id)
          : notifyObjectiveApproved(objective.employeeId, user!.name, objective.title, objective.id)
      );
    }
    applyObjectiveStatusLocally(objectiveId, { status: ObjectiveStatus.APPROVED });
    clearPendingApprovalsCache();
    reload();
  }, [applyObjectiveStatusLocally, isLockedByHR, objectives, reload, user]);

  const handleReturn = useCallback(async (objectiveId: string, comments: string) => {
    const objective = objectives.find(o => o.id === objectiveId);
    if (isLockedByHR(objective)) {
      console.warn('[PMSObjectiveList] Refused to return a locked objective:', objectiveId);
      return;
    }
    const result = await GraphQLPMSService.updateObjective(objectiveId, {
      status: ObjectiveStatus.RETURNED,
      managerComments: comments,
    }, {
      action: objective?.managerComments ? 'Objective Change Request Rejected' : 'Returned',
      changedBy: user!.email,
      changedByName: user!.name,
      previousObjective: objective,
    });
    // Same as handleApprove: confirm the write before notifying or updating locally.
    if (!result.success) {
      console.error('[PMSObjectiveList] Failed to return objective:', result.errors);
      reload();
      return;
    }
    if (objective) {
      await safeNotify(
        objective.managerComments
          ? notifyObjectiveChangeRequestRejected(objective.employeeId, user!.name, objective.title, objective.id, comments)
          : notifyObjectiveReturned(objective.employeeId, user!.name, objective.title, objective.id, comments)
      );
    }
    applyObjectiveStatusLocally(objectiveId, { status: ObjectiveStatus.RETURNED, managerComments: comments });
    clearPendingApprovalsCache();
    reload();
  }, [applyObjectiveStatusLocally, isLockedByHR, objectives, reload, user]);

  const handleApproveAll = useCallback(async (objectiveIds: string[]) => {
    await Promise.all(
      objectiveIds.map(id =>
        handleApprove(id)
      )
    );
    reload();
  }, [handleApprove, reload]);

  const handleReturnAll = useCallback(async (objectiveIds: string[], comments: string) => {
    await Promise.all(
      objectiveIds.map(id =>
        handleReturn(id, comments)
      )
    );
    reload();
  }, [handleReturn, reload]);

  // Grouped view for manager viewing a specific team member
  if (employeeId) {
    return (
      <div className="pms-team-member-view">
        <div className="pms-team-member-view__header">
          <button
            className="pms-team-member-view__back"
            onClick={() => navigate('/performance/team')}
          >
            ← Back to Team
          </button>
        </div>
        {/*
          Says why the approve/return actions below are unavailable. The actions are withdrawn
          when HR has locked the cycle (see isLockedByHR), and without this the manager simply
          found a list of submitted objectives with no way to act on them and no reason given.
          Rendered only when something in the list is actually locked.
        */}
        {objectives.some(o => isLockedByHR(o)) && (
          <p className="pms-team-member-view__locked-note">{OBJECTIVES_LOCKED_MESSAGE}</p>
        )}
        <EmployeeSubmissionPanel
          objectives={objectives}
          loading={loading}
          onObjectiveClick={handleObjectiveClick}
          onApprove={handleApprove}
          onReturn={handleReturn}
          onApproveAll={handleApproveAll}
          onReturnAll={handleReturnAll}
        />
        {/* Objective Detail Drawer */}
        <RightSidePanel
          isOpen={!!selectedObjective}
          title={selectedObjective?.title || 'Objective Detail'}
          onClose={() => setSelectedObjective(null)}
          wide
        >
          {selectedObjective && (
            <ObjectiveDetail
              objective={selectedObjective}
              milestones={detailMilestones}
              versions={detailVersions}
              chatMessages={detailChat}
              attachments={detailAttachments}
              userRole={mapUserRoleToPMSRole(user!.role)}
              userId={user!.email}
              isReadOnly={isLockedByHR(selectedObjective)}
              onApprove={selectedObjective.status === ObjectiveStatus.SUBMITTED && !isLockedByHR(selectedObjective) ? async () => {
                await handleApprove(selectedObjective.id);
                setSelectedObjective(null);
              } : undefined}
              onReturn={selectedObjective.status === ObjectiveStatus.SUBMITTED && !isLockedByHR(selectedObjective) ? async (comments: string) => {
                await handleReturn(selectedObjective.id, comments);
                setSelectedObjective(null);
              } : undefined}
              onSendMessage={async (content: string) => {
                await GraphQLPMSService.sendMessage(
                  { threadType: ChatThreadType.OBJECTIVE, threadId: selectedObjective.id, content },
                  user!.email, user!.name
                );
              }}
              onUploadAttachment={async (file: File) => {
                await GraphQLPMSService.addAttachment(
                  {
                    parentType: AttachmentParentType.OBJECTIVE,
                    parentId: selectedObjective.id,
                    fileName: file.name,
                    fileType: file.type || file.name.split('.').pop() || '',
                    fileSize: file.size,
                    s3Key: `pms-objectives/${selectedObjective.id}/attachments/${file.name}`,
                  },
                  user!.email
                );
              }}
            />
          )}
        </RightSidePanel>
      </div>
    );
  }

  // Pending Approvals — clean queue table
  return (
    <>
      <div className="pms-approvals">
        <div className="pms-approvals__header">
          <div>
            <h1 className="pms-approvals__title">Pending Approvals</h1>
            <p className="pms-approvals__subtitle">Review and approve submitted employee objectives.</p>
          </div>
        </div>

        {/* Filter Chips */}
        <div className="pms-approvals__chips">
          {approvalFilters.map((filter) => {
            const count = approvalSummary.counts.get(filter) || 0;
            const isActive = statusFilter === filter;
            return (
              <button
                key={filter}
                className={`pms-approvals__chip ${isActive ? 'pms-approvals__chip--active' : ''}`}
                onClick={() => setStatusFilter(filter)}
              >
                {filter.replace(/_/g, ' ')}
                <span className="pms-approvals__chip-count">{count}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <p className="pms-approvals__loading">Loading objectives...</p>
        ) : approvalSummary.groupedEntries.length === 0 ? (
          <p className="pms-approvals__empty">No pending approvals found.</p>
        ) : (
          <div className="pms-approvals__employee-list">
            {approvalSummary.groupedEntries.map(([empId, empObjectives]) => {
                const isExpanded = expandedApprovalEmployee === empId;
                const empName = empObjectives[0]?.employeeName || empId;
                const avgProgress = Math.round(empObjectives.reduce((s, o) => s + o.progressPercentage, 0) / empObjectives.length);
                return (
                  <div key={empId} className="pms-approvals__emp-group">
                    <button
                      className={`pms-approvals__emp-header ${isExpanded ? 'pms-approvals__emp-header--expanded' : ''}`}
                      onClick={() => setExpandedApprovalEmployee(isExpanded ? null : empId)}
                    >
                      <span className="pms-approvals__emp-chevron">{isExpanded ? '\u25BC' : '\u25B6'}</span>
                      <div className="pms-approvals__emp-info">
                        <strong>{empName}</strong>
                        <small>{empId}</small>
                      </div>
                      <span className="pms-approvals__emp-count">{empObjectives.length} objective{empObjectives.length !== 1 ? 's' : ''}</span>
                      <span className="pms-approvals__emp-progress">{avgProgress}% avg</span>
                    </button>
                    {isExpanded && (
                      <div className="pms-approvals__obj-list">
                        {empObjectives.map(obj => (
                          <div key={obj.id} className="pms-approvals__obj-card">
                            <div className="pms-approvals__obj-card-top">
                              <div className="pms-approvals__obj-card-info" onClick={() => handleObjectiveClick(obj)} style={{ cursor: 'pointer' }}>
                                <strong>{obj.title}</strong>
                                <span>{obj.description?.slice(0, 80)}{obj.description && obj.description.length > 80 ? '...' : ''}</span>
                              </div>
                              <span className="pms-approvals__status-badge">{obj.status.replace('_', ' ')}</span>
                            </div>
                            <div className="pms-approvals__obj-card-meta">
                              <span>Weightage: {obj.weightage}%</span>
                              <span>Submitted: {new Date(obj.updatedAt || obj.createdAt).toLocaleDateString()}</span>
                            </div>
                            <div className="pms-approvals__obj-card-progress">
                              <div className="pms-approvals__progress-bar">
                                <div className="pms-approvals__progress-fill" style={{ width: `${obj.progressPercentage}%` }} />
                              </div>
                              <span>{obj.progressPercentage}%</span>
                            </div>
                            {obj.status === ObjectiveStatus.SUBMITTED && (
                              <div className="pms-approvals__obj-card-actions">
                                <button className="pms-approvals__btn pms-approvals__btn--approve" onClick={() => handleApprove(obj.id)}>Approve</button>
                                <button className="pms-approvals__btn pms-approvals__btn--return" onClick={() => handleReturn(obj.id, 'Returned for review')}>Return</button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
            })}
          </div>
        )}
      </div>
      {/* Objective Detail Drawer */}
      <RightSidePanel
        isOpen={!!selectedObjective}
        title={selectedObjective?.title || 'Objective Detail'}
        onClose={() => setSelectedObjective(null)}
        wide
      >
        {selectedObjective && (
          <ObjectiveDetail
            objective={selectedObjective}
            milestones={detailMilestones}
            versions={detailVersions}
            chatMessages={detailChat}
            attachments={detailAttachments}
            userRole={mapUserRoleToPMSRole(user!.role)}
            userId={user!.email}
            isReadOnly={isLockedByHR(selectedObjective)}
            onApprove={selectedObjective.status === ObjectiveStatus.SUBMITTED && !isLockedByHR(selectedObjective) ? async () => {
              await handleApprove(selectedObjective.id);
              setSelectedObjective(null);
            } : undefined}
            onReturn={selectedObjective.status === ObjectiveStatus.SUBMITTED && !isLockedByHR(selectedObjective) ? async (comments: string) => {
              await handleReturn(selectedObjective.id, comments);
              setSelectedObjective(null);
            } : undefined}
            onSendMessage={async (content: string) => {
              await GraphQLPMSService.sendMessage(
                { threadType: ChatThreadType.OBJECTIVE, threadId: selectedObjective.id, content },
                user!.email, user!.name
              );
            }}
          />
        )}
      </RightSidePanel>
    </>
  );
};

// ─── Review Form Route ───────────────────────────────────────────────────────

export const PMSReviewFormRoute: React.FC = () => {
  const { empId } = useParams<{ empId: string }>();

  // If no specific employee selected, show the Team Reviews dashboard
  if (!empId) {
    return <TeamReviews mode="team" />;
  }

  // Otherwise show the review form for a specific employee
  return <PMSReviewFormForEmployee empId={empId} />;
};

const PMSReviewFormForEmployee: React.FC<{ empId: string }> = ({ empId }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeCycleId, setActiveCycleId] = useState('');
  const [childCycle, setChildCycle] = useState<ReviewCycle | undefined>(undefined);
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [milestonesByObjective, setMilestonesByObjective] = useState<Record<string, Milestone[]>>({});
  const [existingReview, setExistingReview] = useState<Review | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const targetEmployeeId = empId;

  // reviewType tracks whichever child cycle is actually open right now, so this one
  // route works for both Mid-Year and Year-End instead of only ever handling Year-End.
  const reviewType = childCycle?.reviewCycleType === ReviewCycleType.YEAR_END
    ? ReviewType.END_YEAR
    : ReviewType.MID_YEAR;

  useEffect(() => {
    (async () => {
      // Get the active annual cycle for review context, plus whichever child cycle
      // under it currently has reviewPhase OPEN — reviewPhase lives on the child, not
      // the annual parent. See the PMS plan doc for why these are deliberately
      // separate. If none are open, default to the Year-End child (preserves the
      // route's original behavior when nothing has been explicitly opened yet) so the
      // gate message below can still say something meaningful.
      const active = await GraphQLPMSService.getActiveAnnualCycle();
      if (active) {
        setActiveCycleId(active.id);
        const allCycles = await GraphQLPMSService.getAllReviewCycles();
        const childCycles = allCycles.filter(c => c.annualCycleId === active.id);
        const openChild = childCycles.find(c => c.reviewPhase === ReviewPhase.OPEN);
        const yearEndChild = childCycles.find(c => c.reviewCycleType === ReviewCycleType.YEAR_END);
        setChildCycle(openChild || yearEndChild || childCycles[0]);
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!targetEmployeeId || !activeCycleId) return;
    (async () => {
      const objs = await GraphQLPMSService.getEmployeeObjectives(targetEmployeeId, activeCycleId);
      setObjectives(objs);
      const msMap: Record<string, Milestone[]> = {};
      await Promise.all(objs.map(async (obj) => {
        msMap[obj.id] = await GraphQLPMSService.getMilestones(obj.id);
      }));
      setMilestonesByObjective(msMap);

      const reviews = await GraphQLPMSService.getEmployeeReviews(targetEmployeeId, activeCycleId);
      setExistingReview(reviews.find(r => r.reviewType === reviewType));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetEmployeeId, activeCycleId, reviewType]);

  const employeeName = objectives.length > 0 ? objectives[0].employeeName : 'Employee';

  if (loading) {
    return <InlineLoader />;
  }

  if (!user || !activeCycleId || !targetEmployeeId) {
    return <p>Select a team member to review.</p>;
  }

  if (!canManagerReview(childCycle, existingReview)) {
    const progress = getSelfReviewProgress(existingReview);
    let message = 'This review is not open yet.';
    if (childCycle?.reviewPhase === ReviewPhase.CLOSED) {
      message = 'This review has been closed by HR.';
    } else if (progress === SelfReviewProgress.NOT_STARTED) {
      message = `Waiting for ${employeeName} to submit their self-review.`;
    } else if (progress === SelfReviewProgress.MANAGER_SUBMITTED) {
      message = 'You have already submitted this review.';
    }
    return <p className="pms-review-form__gate-message">{message}</p>;
  }

  return (
    <ReviewForm
      reviewType={reviewType}
      employeeId={targetEmployeeId}
      employeeName={employeeName}
      reviewCycleId={activeCycleId}
      objectives={objectives}
      milestonesByObjective={milestonesByObjective}
      employeeSelfReview={existingReview}
      onSubmit={async (input) => {
        const managerSubmittedAt = new Date().toISOString();
        const updated = existingReview
          ? await GraphQLPMSService.updateReview(existingReview.id, {
              objectiveRatings: input.objectiveRatings,
              overallRating: input.overallRating,
              overallComments: input.overallComments,
              developmentSuggestions: input.developmentSuggestions,
              managerSubmittedAt,
              reviewerId: user.email,
              reviewerName: user.name,
              workflowStatus: ReviewWorkflowStatus.COMPLETED,
            })
          : await GraphQLPMSService.createReview(input, user.email, user.name);
        if (updated.success && input.reviewType === ReviewType.END_YEAR) {
          await Promise.all(input.objectiveRatings.map((rating) => {
            const objective = objectives.find(o => o.id === rating.objectiveId);
            if (!objective) return Promise.resolve();
            return GraphQLPMSService.updateObjective(objective.id, {
              finalProgressPercentage: rating.finalProgressPercentage,
              // Never `|| rating.comments` — that is the employee's self-review note, and this
              // writes the shared Objective record that the next review cycle's manager form
              // reads back. Same leak as the Team Reviews submit path.
              managerFinalComment: rating.managerFinalComment,
              evaluationSummary: rating.evaluationSummary,
              status: ObjectiveStatus.COMPLETED,
            }, {
              action: 'Review Submitted',
              changedBy: user.email,
              changedByName: user.name,
              previousObjective: objective,
            });
          }));
          await safeNotify(notifyFinalReviewAvailable(input.employeeId, user.name, 'current review cycle'));
        } else if (updated.success && input.reviewType === ReviewType.MID_YEAR) {
          await safeNotify(notifyMidYearReviewReminder(input.employeeId, user.name, 'current review cycle'));
        }
        navigate('/performance/team');
      }}
      onCancel={() => navigate('/performance/team')}
    />
  );
};

// ─── HR Admin Dashboard Route ────────────────────────────────────────────────

export const PMSHRAdminDashboardRoute: React.FC = () => {
  const [allCycles, setAllCycles] = useState<ReviewCycle[]>([]);
  const [selectedCycleId, setSelectedCycleId] = useState('');
  const [allObjectives, setAllObjectives] = useState<Objective[]>([]);
  const [allReviews, setAllReviews] = useState<Review[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const reload = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    (async () => {
      const cycles = await GraphQLPMSService.getAllReviewCycles();
      // Analytics uses HR-created child review cycles. Annual records are containers,
      // while Draft, Active, Locked, and Completed child cycles are all reportable.
      const analyticsCycles = cycles
        .filter(cycle => Boolean(cycle.annualCycleId))
        .sort((a, b) => {
          const activeDifference = Number(b.status === ReviewCycleStatus.ACTIVE) - Number(a.status === ReviewCycleStatus.ACTIVE);
          return activeDifference || b.startDate.localeCompare(a.startDate);
        });
      const excludedCycles = cycles
        .filter(cycle => !cycle.annualCycleId)
        .map(cycle => ({ id: cycle.id, name: cycle.name, reason: 'Annual parent cycle' }));

      console.groupCollapsed('[HR Analytics][Review Cycle Dropdown] Validation');
      console.info('Source API', 'GraphQLPMSService.getAllReviewCycles -> listPMSReviewCycles (fully paginated)');
      console.info('Total review cycle records returned by API', cycles.length);
      console.info('Cycles shown in dropdown', analyticsCycles.length);
      console.info('Applied filters', {
        hierarchyFilter: 'annualCycleId must be present (HR-created child review cycle)',
        statusFilter: 'None: Draft, Active, Locked, and Completed are included',
        reviewTypeFilter: 'None: YEAR_END, HIGH_LEVEL, and unspecified types are included',
        pagination: 'All nextToken pages loaded; 100 records per request',
        slicing: 'None',
      });
      console.table(analyticsCycles.map(cycle => ({
        id: cycle.id,
        name: cycle.name,
        reviewType: cycle.reviewCycleType || 'Unspecified',
        status: cycle.status,
        annualCycleId: cycle.annualCycleId,
        shown: true,
      })));
      if (excludedCycles.length) console.table(excludedCycles);
      console.groupEnd();

      setAllCycles(analyticsCycles);
      setSelectedCycleId(currentId => {
        if (analyticsCycles.some(cycle => cycle.id === currentId)) return currentId;
        return analyticsCycles[0]?.id ?? '';
      });
    })();
  }, [refreshKey]);

  useEffect(() => {
    if (!selectedCycleId) return;
    (async () => {
      const [objs, revs] = await Promise.all([
        GraphQLPMSService.getObjectivesByReviewCycle(selectedCycleId),
        GraphQLPMSService.getReviewsByReviewCycle(selectedCycleId),
      ]);
      setAllObjectives(objs);
      setAllReviews(revs);
    })();
  }, [selectedCycleId, refreshKey]);

  return (
    <HRAdminDashboard
      allObjectives={allObjectives}
      allReviews={allReviews}
      reviewCycles={allCycles}
      selectedCycleId={selectedCycleId}
      onCycleChange={setSelectedCycleId}
      onRefresh={reload}
    />
  );
};

// ─── Review Cycle Config Route ───────────────────────────────────────────────

export const PMSReviewCycleConfigRoute: React.FC = () => {
  return <ReviewCycleConfig />;
};

// ─── Performance Reports Route ───────────────────────────────────────────────

export const PMSPerformanceReportsRoute: React.FC = () => {
  const [allCycles, setAllCycles] = useState<ReviewCycle[]>([]);
  const [selectedCycleId, setSelectedCycleId] = useState('');
  const [allObjectives, setAllObjectives] = useState<Objective[]>([]);
  const [allReviews, setAllReviews] = useState<Review[]>([]);

  useEffect(() => {
    (async () => {
      const cycles = await GraphQLPMSService.getAllReviewCycles();
      // Only show ACTIVE annual cycles (parent) in the dropdown
      const annualOnly = cycles.filter(c => !c.annualCycleId);
      const activeCycles = annualOnly.filter(c => c.status === ReviewCycleStatus.ACTIVE);
      setAllCycles(activeCycles);
      setSelectedCycleId(activeCycles[0]?.id ?? '');
    })();
  }, []);

  useEffect(() => {
    if (!selectedCycleId) return;
    (async () => {
      const [objs, revs] = await Promise.all([
        GraphQLPMSService.getObjectivesByReviewCycle(selectedCycleId),
        GraphQLPMSService.getReviewsByReviewCycle(selectedCycleId),
      ]);
      setAllObjectives(objs);
      setAllReviews(revs);
    })();
  }, [selectedCycleId]);

  return (
    <PerformanceReports
      allObjectives={allObjectives}
      allReviews={allReviews}
      reviewCycles={allCycles}
      selectedCycleId={selectedCycleId}
      onCycleChange={setSelectedCycleId}
    />
  );
};

// ─── Objective Lock Manager Route ────────────────────────────────────────────

export const PMSObjectiveLockManagerRoute: React.FC = () => {
  const { user } = useAuth();
  const [cycles, setCycles] = useState<ReviewCycle[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const reload = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    (async () => {
      const allCycles = await GraphQLPMSService.getAllReviewCycles();
      // Only show annual cycles (parent) for lock management
      setCycles(allCycles.filter(c => !c.annualCycleId));
    })();
  }, [refreshKey]);

  if (!user) return null;

  return (
    <ObjectiveLockManager
      reviewCycles={cycles}
      auditLog={[]}
      onLock={async (cycleId) => {
        await GraphQLPMSService.lockObjectivesForCycle(cycleId);
        reload();
      }}
      onUnlock={async (cycleId) => {
        await GraphQLPMSService.unlockObjectivesForCycle(cycleId);
        reload();
      }}
    />
  );
};
